<script>
import { defineComponent, computed, ref } from 'vue'
import { scaleLinear, scaleTime, extent, timeFormat, max, min } from 'd3'
import { useElementSize } from '@vueuse/core'

import LineMark from './marks/Line.vue'
import AxisBottom from './AxisBottom.vue'
import AxisLeft from './AxisLeft.vue'
import AxisRight from './AxisRight.vue'
import Ranks from './Ranks.vue'
import Tooltips from './Tooltips.vue'

const defaultOptions = {
    margins: {
        top: 30,
        right: 10,
        bottom: 30,
        left: 30
    },
    ranks: 10,
    levels: 100,
    width: 0,
    height: 0,
    colors: ['#eee'],
    xLabels: {
        format: timeFormat('%B')
    },
    yLabels: {
        legend: 'Neurons earned'
    },
    marks: {
        lineColor: '#000',
    }
}

export default defineComponent({
    name: 'ProgressLine',
    components: { AxisBottom, AxisLeft, AxisRight, LineMark, Ranks, Tooltips },
    props: {
        series: {
            type: Array,
            default: () => ([])
        },
        options: {
            type: Object,
            default: () => defaultOptions
        },
    },
    setup(props) {
        const ticks = ref(5)
        const container = ref(null)
        const parent = computed(() => container.value && container.value.parentElement)
        const settings = computed(() => Object.assign(defaultOptions, props.options))
        const margins = computed(() => settings.value.margins)
        const ranks = computed(() => settings.value.ranks)
        const levels = computed(() => settings.value.levels)
        const { width: elWidth } = useElementSize(parent)
        const width = computed(() => props.options.width || elWidth.value)
        const height = computed(() => props.options.height || width.value * 0.6)
        const innerHeight = computed(() => {
            return height.value - margins.value.top - margins.value.bottom
        })
        const innerWidth = computed(() => {
            return width.value - margins.value.left - margins.value.right
        })
        const flatten = d => d.reduce((acc, next) => [...acc, ...next.history], [])

        // Accessors
        const xAccessor = d => d.date
        const yAccessor = d => d.level

        const minRank = computed(() => min(props.series.map(d => d.rank)))
        const maxRank = computed(() => max(props.series.map(d => d.rank)))
        const minLevel = computed(() => min(flatten(props.series).map(yAccessor)))
        const maxLevel = computed(() => max(flatten(props.series).map(yAccessor)))

        const colorsRange = computed(() => settings.value.colors.reduce((acc, color) => {
            acc.push(...(new Array(levels.value / ranks.value)).fill(color))
            return acc
        }, []))

        // Scales
        const yScale = computed(() => scaleLinear()
            .domain([
                minLevel.value > 5 ? minLevel.value - 4 : 0,
                maxLevel.value < levels.value - 2 ? maxLevel.value + 2 : maxLevel.value
            ])
            .range([innerHeight.value, 0])
            .nice()
        )

        const xScale = computed(() => scaleTime()
            .domain(extent(flatten(props.series), xAccessor))
            .range([0, innerWidth.value])
        )

        return {
            width,
            height,
            container,
            maxLevel,
            settings,
            innerHeight,
            innerWidth,
            margins,
            ticks,
            ranks,
            levels,
            yScale,
            xScale,
            xAccessor,
            yAccessor,
            minRank,
            maxRank,
            minLevel,
            maxLevel,
            colorsRange,
        }
    },
})
</script>

<template>
    <svg ref="container" :viewBox="`0 0 ${width} ${height}`">
        <g v-if="width" :transform="`translate(${margins.left},${margins.top})`" :view-box="`0 0 ${width} ${height}`">
            <AxisLeft :height="innerHeight" :width="innerWidth" :scale="yScale" :options="settings.yLabels" />
            <AxisRight :scale="yScale" :height="innerHeight" :width="innerWidth" />
            <AxisBottom :scale="xScale" :height="innerHeight" :width="innerWidth" :format="xScale.tickFormat(1)" />
            <Ranks v-if="yScale.ticks" :height="innerHeight" :width="innerWidth" :ranks="maxRank" :levels="maxLevel" :colors="colorsRange" :yScale="yScale" />
            <LineMark :xAccessor="xAccessor" :yAccessor="yAccessor" :width="innerWidth" :xScale="xScale" :yScale="yScale" :series="series" :lineColor="settings.marks.lineColor" />
            <Tooltips :yAccessor="yAccessor" :yScale="yScale" :series="series" :color="settings.marks.lineColor" :width="innerWidth" :height="innerHeight" />
        </g>
    </svg>
</template>
